import React, { useEffect, useMemo, useState } from 'react';
import { Platform, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { avatarDefault } from '@/assets/images';
import { Stack } from '@/components/layout';
import {
  Avatar,
  LabeledDivider,
  PressableSurface,
  showAlert,
  SvgIcon,
  Text,
} from '@/components/ui';
import { hitSlop } from '@/constants';
import { STRINGS } from '@/constants/strings';
import {
  AuthBrandLogo,
  AuthLegalPopup,
  AuthPrimaryButton,
  AuthScreenBackground,
  AuthTermsText,
  AuthTextField,
} from '@/features/auth/components';
import { SocialAuthButton } from '@/features/auth/components/social-auth-button';
import { useBiometric } from '@/features/auth/hooks/use-biometric';
import { useLegalPopup } from '@/features/auth/hooks/use-legal-popup';
import { useLogin } from '@/features/auth/hooks/use-login';
import { authenticateBiometric } from '@/features/auth/services/biometric-auth';
import { useGuardianProfileQuery } from '@/features/guardian/queries';
import { useCoachQuery } from '@/features/home/queries';
import { createStyles, useScaling, useUnAuthStackNavigation } from '@/hooks';
import { useEnvUrls } from '@/libs/config/env-urls';
import { signOut } from '@/libs/firebase/auth/auth-helpers';
import { captureException } from '@/libs/logger';
import { ensureOnline } from '@/libs/network';
import { useUiStore } from '@/stores';
import { useSessionStore } from '@/stores/session-store';
import { colors, guardianColors } from '@/theme/colors';
import { withVerifyAccount } from '@/utils/helper';
import { useShallow } from 'zustand/shallow';

export function LoginWithBiometricsScreen() {
  const { scale } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useUnAuthStackNavigation();
  const legal = useLegalPopup();
  const { kind, enabled: biometricEnabled } = useBiometric();

  const user = useSessionStore(useShallow(s => s.user));
  const activeRole = useSessionStore(useShallow(s => s.activeRole));
  const guardianProfile = useGuardianProfileQuery(user?.uid);
  const { data: coach } = useCoachQuery(user?.uid);
  const setSoftLoggedOut = useSessionStore(state => state.setSoftLoggedOut);

  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const { pictureUrl: pictureUrlBase } = useEnvUrls();

  const avatarSource = useMemo(() => {
    const pictureUrl = pictureUrlBase?.trim();
    const uri =
      pictureUrl && coach?.id
        ? `${pictureUrl}${coach.id}.jpg?alt=media&updated_at=${coach.updated_at}`
        : undefined;
    if (!uri) return undefined;
    return { uri };
  }, [coach?.id, coach?.updated_at, pictureUrlBase]);

  const profileAvatar = useMemo(() => {
    if (activeRole === 'coach') return avatarSource ?? avatarDefault;

    const photoUrl = guardianProfile.data?.photoUrl?.trim();
    return photoUrl ? { uri: photoUrl } : avatarDefault;
  }, [activeRole, avatarSource, guardianProfile.data?.photoUrl]);

  // Reuse the shared sign-in logic. The email is fixed to the signed-in account
  // (this is a re-auth screen), so only the password is user-editable here.
  const { onChangeEmail, password, onChangePassword, onBlurPassword, handleSubmit, errors } =
    useLogin();

  const [secureTextEntry, setSecureTextEntry] = useState(true);

  const strings = STRINGS.auth.loginWithBiometrics;

  // Seed the login form with the signed-in user's email so `handleSubmit` signs
  // in against the right account.
  useEffect(() => {
    if (user?.email) onChangeEmail(user.email);
  }, [user?.email, onChangeEmail]);

  // Greet the signed-in user by their first name; fall back gracefully when the
  // display name is missing (e.g. social sign-in without a name).
  const greeting = useMemo(() => {
    const firstName = activeRole === 'coach' ? coach?.firstname : guardianProfile?.data?.firstname;
    return firstName ? strings.greeting.replace('{name}', firstName) : strings.greetingFallback;
  }, [
    activeRole,
    coach?.firstname,
    guardianProfile?.data?.firstname,
    strings.greeting,
    strings.greetingFallback,
  ]);

  // Face ID vs fingerprint icon, mirroring the device's enrolled biometric.
  const biometricIcon = kind === 'face' ? 'IcFaceId' : 'IcFingerPrint';

  const handleForgotPassword = () => {
    navigation.navigate('ForgotPassword');
  };

  // Hard logout: really sign the user out (clears the session) and return to Welcome.
  const handleLogout = async () => {
    if (Platform.OS === 'web') showGlobalLoading();
    try {
      await signOut();
    } finally {
      navigation.reset({ index: 0, routes: [{ name: 'Welcome' }] });
      if (Platform.OS === 'web') hideGlobalLoading();
    }
  };

  // Biometric unlock: only when this account has biometric login turned on. The Firebase
  // session is still alive during a soft logout, so passing the OS prompt just clears the
  // lock and lets `withUnAuth` route back into the app.
  const handleBiometricLogin = async () => {
    try {
      showGlobalLoading();
      await ensureOnline();
      // This account hasn't opted into biometric login — point the user at where to enable it.
      if (!biometricEnabled) {
        showAlert({
          title: strings.biometricNotEnabledTitle,
          message: strings.biometricNotEnabledMessage,
          buttons: [{ text: STRINGS.errors.ok }],
        });
        return;
      }
      const authenticated = await withVerifyAccount(() =>
        authenticateBiometric(strings.biometricPrompt),
      );
      await ensureOnline();
      if (authenticated) setSoftLoggedOut(false);
    } catch (error) {
      captureException(
        {
          error_payload: error,
        },
        { tags: { usk_log_type: 'handle_biometrics_error' } },
      );
    } finally {
      hideGlobalLoading();
    }
  };

  const top = insets.top > 0 ? insets.top + scale({ phone: 16, tablet: 58 }) : scale(60);

  return (
    <AuthScreenBackground scrollable>
      <Stack
        flex={1}
        width={scale({ phone: 335, tablet: 380 })}
        style={{
          paddingTop: top,
          paddingBottom: insets.bottom + scale(12),
        }}>
        {/* Logo */}
        <Stack align="center" marginTop={scale({ phone: 26, tablet: 0 })}>
          <AuthBrandLogo portrait width={scale(154)} height={scale(84)} />
        </Stack>

        {/* Avatar + logout badge */}
        <Stack align="center" marginTop={scale({ phone: 28, tablet: 32 })}>
          <Stack>
            <Avatar size={scale(88)} borderColor={colors['0']} borderWidth={scale(2)}>
              <Avatar.Image source={profileAvatar} />
            </Avatar>
            <TouchableOpacity
              onPress={handleLogout}
              hitSlop={hitSlop}
              accessibilityRole="button"
              accessibilityLabel={strings.logoutAccessibilityLabel}
              style={styles.logoutBadge}>
              <SvgIcon name="IcLogout" size={scale(16)} color={guardianColors.Error} />
            </TouchableOpacity>
          </Stack>

          {/* Greeting */}
          <Text
            variant="Font Guardian App/Heading/Heading H6 18px"
            style={styles.greeting}
            accessibilityRole="header">
            {greeting}
          </Text>
        </Stack>

        {/* Password */}
        <Stack marginTop={scale(30)}>
          <AuthTextField
            label={strings.labelPassword}
            iconName="IcPasswordCheck"
            value={password}
            onChangeText={onChangePassword}
            onBlur={onBlurPassword}
            required
            error={errors.password?.message}
            placeholder={strings.placeholderPassword}
            secureTextEntry={secureTextEntry}
            onSecureTextEntryChange={setSecureTextEntry}
            fieldProps={{ maxLength: 50 }}
          />
        </Stack>

        {/* Forgot password */}
        <TouchableOpacity
          onPress={handleForgotPassword}
          hitSlop={hitSlop}
          style={styles.forgotWrapper}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.forgotText}>
            {strings.forgotPassword}
          </Text>
        </TouchableOpacity>

        {/* Login + biometric */}
        <Stack direction="row" align="center" gap={scale(16)} marginTop={scale(24)}>
          <Stack flex={1}>
            <AuthPrimaryButton onPress={handleSubmit}>{strings.buttonLogin}</AuthPrimaryButton>
          </Stack>
          {Platform.OS !== 'web' && (
            <PressableSurface
              size={49}
              borderRadius={scale(12)}
              backgroundColor={colors['0']}
              onPress={handleBiometricLogin}
              accessibilityLabel={strings.biometricAccessibilityLabel}
              style={styles.biometricButton}>
              <PressableSurface.Icon
                name={biometricIcon}
                size={30}
                color={guardianColors['Main/Main']}
              />
            </PressableSurface>
          )}
        </Stack>

        {/* Flexible spacer pushes the social actions to the bottom */}
        <Stack flex={1} minHeight={scale(40)} />

        {/* Or login with */}
        <LabeledDivider
          color={colors['3']}
          textVariant="Font Guardian App/Subtitle/Subtitle 12 Regular"
          textColor={colors['6']}>
          {strings.orLoginWith}
        </LabeledDivider>

        {/* Social auth */}
        <Stack direction="row" justify="center" gap={scale(16)} marginTop={scale(16)}>
          <SocialAuthButton provider="google" />
          <SocialAuthButton provider="apple" />
        </Stack>

        {/* Terms */}
        <AuthTermsText
          prefix={strings.termsPrefix}
          termsOfService={strings.termsOfService}
          conjunction={strings.termsConjunction}
          privacyPolicy={strings.privacyPolicy}
          onPressTermsOfService={legal.openTerms}
          onPressPrivacyPolicy={legal.openPrivacy}
          breakAfterPrefix
          style={styles.terms}
        />
      </Stack>

      <AuthLegalPopup visible={legal.visible} document={legal.document} onClose={legal.close} />
    </AuthScreenBackground>
  );
}

const useStyles = createStyles(({ scale }) => ({
  logoutBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: scale(24),
    height: scale(24),
    borderRadius: scale(12),
    backgroundColor: colors['0'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  greeting: {
    marginTop: scale(6),
    color: colors['10'],
    textAlign: 'center',
  },
  forgotWrapper: {
    alignSelf: 'flex-end',
    marginTop: scale(12),
  },
  forgotText: {
    color: guardianColors['Main/Main'],
    textAlign: 'right',
  },
  biometricButton: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 0.5,
    elevation: 1,
  },
  terms: {
    marginTop: scale(30),
  },
}));

export default LoginWithBiometricsScreen;
